import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import './Integrations.css';
import {
	EmptyState,
	IntegrationSettings,
	Link,
	Spinner,
	UnprocessableError,
	useDeferredLoading,
} from '@anticatrade/shared';
import SlButton from '@shoelace-style/shoelace/dist/react/button/index.js';
import { marked } from 'marked';
import { AppContext, appContext } from '../../../context/AppContext';
import { Heading } from '../../base/Heading/Heading';
import { useIntegration } from './useIntegration';
import { integrationForms } from './integrationForms';

const Integration = () => {
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [settings, setSettings] = useState<IntegrationSettings>({});

	const params = useParams();
	const key = params.key;
	const id = params.id != null ? Number(params.id) : undefined;

	const { t } = useTranslation();
	const { showStatus, handleError, redirect } = useContext(
		appContext as React.Context<AppContext>,
	);
	const { isLoading, isEdit, metadata, integration, save } = useIntegration(key, id);
	const { showSpinner, showContent } = useDeferredLoading(isLoading);

	useEffect(() => {
		if (integration != null) {
			setSettings(integration.settings);
		}
	}, [integration]);

	const guideHTML = useMemo(
		() =>
			metadata != null && metadata.guide !== ''
				? (marked.parse(metadata.guide) as string)
				: '',
		[metadata],
	);

	const form = metadata != null ? integrationForms[metadata.key] : undefined;
	const hasRequiredSettings = form == null || form.isComplete(settings);

	const onSave = async () => {
		setIsSaving(true);
		try {
			await save(form != null ? settings : {});
		} catch (err) {
			if (err instanceof UnprocessableError && err.code === 'IntegrationExists') {
				showStatus({
					variant: 'warning',
					icon: 'exclamation-triangle',
					text: t('This integration is already connected'),
				});
			} else if (
				err instanceof UnprocessableError &&
				err.code === 'IntegrationUnauthorized'
			) {
				showStatus({
					variant: 'warning',
					icon: 'exclamation-triangle',
					text: t(
						'The website refused the credentials. Check the consumer key and secret.',
					),
				});
			} else if (
				err instanceof UnprocessableError &&
				err.code === 'IntegrationRateLimitReached'
			) {
				showStatus({
					variant: 'warning',
					icon: 'exclamation-triangle',
					text: t('IntegrationRateLimitReached').replace(
						'{destination}',
						metadata?.title ?? '',
					),
				});
			} else {
				handleError(err as Error);
			}
			setIsSaving(false);
			return;
		}
		setIsSaving(false);
		showStatus({
			variant: 'success',
			icon: 'check-circle',
			text: t('Integration saved successfully'),
		});
		redirect('integrations');
	};

	if (showContent && metadata == null) {
		return (
			<div className='integration'>
				<EmptyState
					icon='plug'
					title={t('Integration not found')}
					description={t('This integration is no longer available')}
				>
					<Link path='/integrations'>
						<SlButton>{t('Back to integrations')}</SlButton>
					</Link>
				</EmptyState>
			</div>
		);
	}

	return (
		<div className='integration'>
			{showContent && metadata != null ? (
				<>
					<Heading
						left={
							<div className='integration__heading'>
								<img
									className='integration__logo'
									src={metadata.logoURL}
									alt={metadata.title}
								/>
								<h1 className='integration__title'>
									{isEdit
										? metadata.title
										: t('Connect {integration}').replace(
												'{integration}',
												metadata.title,
											)}
								</h1>
							</div>
						}
					/>
					<div className='integration__info wrapped'>
						<div className='integration__details'>
							{metadata.details.split('\n\n').map((paragraph, i) => (
								<p key={i}>{paragraph}</p>
							))}
						</div>
						{metadata.dailyLimit > 0 && (
							<div className='integration__limit'>
								<span className='integration__limit-value'>
									{metadata.dailyLimit}
								</span>{' '}
								<span className='integration__limit-label'>
									{t('publications per day')}
								</span>
							</div>
						)}
					</div>

					{form != null && <form.Form settings={settings} onChange={setSettings} />}

					<div className='integration__actions'>
						{isEdit && form == null ? (
							<Link path='/integrations'>
								<SlButton variant='default'>{t('Back')}</SlButton>
							</Link>
						) : (
							<>
								<Link path='/integrations'>
									<SlButton>{t('Cancel')}</SlButton>
								</Link>
								<SlButton
									variant='primary'
									loading={isSaving}
									disabled={!hasRequiredSettings}
									onClick={onSave}
								>
									{isEdit ? t('Save') : t('Connect')}
								</SlButton>
							</>
						)}
					</div>

					{guideHTML !== '' && (
						<div
							className='integration__guide'
							dangerouslySetInnerHTML={{ __html: guideHTML }}
						/>
					)}
				</>
			) : showSpinner ? (
				<Spinner />
			) : null}
		</div>
	);
};

export { Integration };
